import { motion } from 'framer-motion'
import { ArrowRight, Sparkles, TrendingUp, Users, Award } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../../context/LanguageContext'
import { useState, useEffect } from 'react'

const Hero = () => {
  const { t } = useLanguage()
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 20,
        y: (e.clientY / window.innerHeight) * 20
      })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const stats = [
    { icon: Award, value: '2', label: t('hero.stats.projects') },
    { icon: Users, value: '35+', label: t('hero.stats.clients') },
    { icon: TrendingUp, value: '150%', label: t('hero.stats.growth') },
    { icon: Sparkles, value: 'New', label: t('hero.stats.experience') }
  ]

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/hero/hero-bg.jpg" 
          alt="Hero Background" 
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-dark via-bronze-950/90 to-dark"></div>
      </div>
      {/* Animated Background Elements - MORE PROFESSIONAL */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Sophisticated gradient orbs */}
        <motion.div
          animate={{
            x: [0, 30, 0],
            y: [0, -30, 0],
            scale: [1, 1.1, 1],
            opacity: [0.15, 0.25, 0.15]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-10 w-[500px] h-[500px] bg-gradient-to-br from-gold-400/30 to-bronze-500/20 rounded-full blur-[100px]"
        />
        <motion.div
          animate={{
            x: [0, -40, 0],
            y: [0, 40, 0],
            scale: [1, 1.15, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute bottom-20 right-10 w-[600px] h-[600px] bg-gradient-to-tl from-bronze-400/30 to-gold-500/20 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            x: [0, 20, 0],
            y: [0, -20, 0],
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-gradient-radial from-gold-300/20 to-transparent rounded-full blur-[80px]"
        />
        
        {/* Elegant floating particles - REDUCED & REFINED */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              width: i % 3 === 0 ? 3 : 2,
              height: i % 3 === 0 ? 3 : 2,
              background: i % 2 === 0 ? 'rgba(212, 175, 55, 0.4)' : 'rgba(184, 134, 11, 0.3)',
              borderRadius: '50%',
              left: `${10 + (i * 7)}%`,
              top: `${15 + (i * 6)}%`,
              boxShadow: '0 0 10px rgba(212, 175, 55, 0.3)'
            }}
            animate={{
              y: [-20, -60, -20],
              x: [0, (i % 2 === 0 ? 15 : -15), 0],
              opacity: [0.3, 0.7, 0.3],
              scale: [1, 1.3, 1]
            }}
            transition={{
              duration: 8 + (i * 0.5),
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.8
            }}
          />
        ))}
        
        {/* Subtle grid lines for depth */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" 
            style={{
              backgroundImage: 'linear-gradient(rgba(212, 175, 55, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(212, 175, 55, 0.1) 1px, transparent 1px)',
              backgroundSize: '100px 100px'
            }}
          />
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-block"
            >
              <span className="px-4 py-2 bg-gold-100 text-gold-700 rounded-full text-sm font-semibold flex items-center space-x-2 w-fit">
                <Sparkles className="w-4 h-4" />
                <span>Premium Digital Marketing</span>
              </span>
            </motion.div>

            <div className="space-y-4">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold leading-tight">
                {t('hero.title')}{' '}
                <span className="text-gradient-gold relative">
                  {t('hero.titleHighlight')}
                  <motion.div
                    className="absolute -bottom-2 left-0 h-1 bg-gradient-gold"
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ delay: 1, duration: 0.8 }}
                  />
                </span>
              </h1>
              <h2 className="text-3xl md:text-4xl font-display text-gold-400">
                {t('hero.subtitle')}
              </h2>
            </div>

            <p className="text-lg text-beige-200 leading-relaxed max-w-xl">
              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/live-chat" className="btn-primary flex items-center justify-center space-x-2 group">
                <span>Konsultim Falas</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/services" className="btn-secondary flex items-center justify-center space-x-2">
                <span>{t('hero.learnMore')}</span>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="text-center"
                >
                  <stat.icon className="w-8 h-8 text-gold-400 mx-auto mb-2" />
                  <div className="text-3xl font-display font-bold text-cream">{stat.value}</div>
                  <div className="text-sm text-beige-300">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - 3D Cards */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative perspective-1000"
          >
            <div className="relative">
              {/* Main Card */}
              <motion.div
                animate={{
                  y: [0, -20, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                className="relative z-20 bg-gradient-to-br from-bronze-900/80 to-dark/80 backdrop-blur-lg rounded-3xl p-8 shadow-gold-lg border border-gold-500/20"
              >
                <div className="aspect-square bg-gradient-to-br from-bronze-900 via-dark to-bronze-950 rounded-2xl relative overflow-hidden flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-gold-500/10 to-transparent"></div>
                  <div className="relative z-10 text-center">
                    <img 
                      src="/logo.png" 
                      alt="Contenta Logo" 
                      className="w-64 h-64 mx-auto mb-6 object-contain drop-shadow-2xl"
                    />
                    <h3 className="text-3xl font-display font-bold text-gold-400 mb-2">Contenta.al</h3>
                    <p className="text-lg text-beige-200">Premium Marketing Agency</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Card 1 */}
              <motion.div
                animate={{
                  y: [0, -30, 0],
                  x: [0, 20, 0]
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                className="absolute -top-10 -right-10 z-10 w-48 h-48 bg-gradient-bronze rounded-2xl shadow-bronze p-6 flex items-center justify-center"
              >
                <div className="text-center text-white">
                  <TrendingUp className="w-12 h-12 mx-auto mb-2" />
                  <p className="font-bold text-lg">Growth</p>
                  <p className="text-sm opacity-90">Focused</p>
                </div>
              </motion.div>

              {/* Floating Card 2 */}
              <motion.div
                animate={{
                  y: [0, 30, 0],
                  x: [0, -15, 0]
                }}
                transition={{
                  duration: 7,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 1
                }}
                className="absolute -bottom-10 -left-10 z-10 w-40 h-40 bg-gradient-gold rounded-2xl shadow-gold p-6 flex items-center justify-center"
              >
                <div className="text-center text-white">
                  <Sparkles className="w-10 h-10 mx-auto mb-2" />
                  <p className="font-bold">Creative</p>
                  <p className="text-xs opacity-90">Excellence</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-gold-500 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-gold-500 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  )
}

export default Hero
